import React, { useState, useRef, useMemo } from 'react';
import { CHART_PALETTE, SURFACE } from './chartColors';

interface BubbleDataItem {
  label: string;
  value: number;
}

interface BubbleChartProps {
  data: BubbleDataItem[];
  height?: number;
  maxBubbles?: number;
  className?: string;
}

interface BubblePosition {
  x: number;
  y: number;
  r: number;
  label: string;
  value: number;
  color: typeof CHART_PALETTE[0];
  index: number;
}

// Deterministic seeded random to avoid layout jumps on re-render
function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const BubbleChart: React.FC<BubbleChartProps> = ({
  data,
  height = 400,
  maxBubbles = 15,
  className = '',
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; label: string; value: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayData = data.slice(0, maxBubbles);
  const total = displayData.reduce((s, d) => s + d.value, 0);
  const maxValue = Math.max(...displayData.map(d => d.value), 1);

  // Stable key for useMemo — only recalculate when actual data content changes
  const dataKey = displayData.map(d => `${d.label}:${d.value}`).join('|');

  const bubbles = useMemo(() => {
    const width = 580;
    const h = height - 20;
    const minRadius = 22;
    const maxRadius = Math.min(72, h / 3);

    // Use deterministic seed based on data content
    const rng = seededRandom(dataKey.length * 31 + displayData.length * 17);

    const items: BubblePosition[] = displayData.map((d, i) => {
      const normalizedSize = d.value / maxValue;
      const r = minRadius + normalizedSize * (maxRadius - minRadius);

      // Distribute items in a spiral-ish pattern for better initial layout
      const angle = (i / displayData.length) * 2 * Math.PI;
      const spread = Math.min(width, h) * 0.25;

      return {
        x: width / 2 + Math.cos(angle) * spread * (0.5 + rng() * 0.5),
        y: h / 2 + Math.sin(angle) * spread * (0.5 + rng() * 0.5),
        r,
        label: d.label,
        value: d.value,
        color: CHART_PALETTE[i % CHART_PALETTE.length],
        index: i,
      };
    });

    // Force simulation — push overlapping bubbles apart
    for (let iter = 0; iter < 150; iter++) {
      for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
          const dx = items[j].x - items[i].x;
          const dy = items[j].y - items[i].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const minDist = items[i].r + items[j].r + 5;

          if (dist < minDist && dist > 0) {
            const overlap = (minDist - dist) / 2;
            const nx = dx / dist;
            const ny = dy / dist;
            items[i].x -= nx * overlap * 0.5;
            items[i].y -= ny * overlap * 0.5;
            items[j].x += nx * overlap * 0.5;
            items[j].y += ny * overlap * 0.5;
          }
        }

        // Pull toward center gently
        const centerPull = 0.012;
        items[i].x += (width / 2 - items[i].x) * centerPull;
        items[i].y += (h / 2 - items[i].y) * centerPull;

        // Keep within bounds
        items[i].x = Math.max(items[i].r + 6, Math.min(width - items[i].r - 6, items[i].x));
        items[i].y = Math.max(items[i].r + 6, Math.min(h - items[i].r - 6, items[i].y));
      }
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, height]);

  if (displayData.length === 0) {
    return <div className="flex items-center justify-center h-full text-gray-400">No data</div>;
  }

  return (
    <div ref={containerRef} className={`relative ${className}`} style={{ height }}>
      <svg width="100%" height="100%" viewBox={`0 0 580 ${height - 20}`} preserveAspectRatio="xMidYMid meet">
        {/* Gradient defs */}
        <defs>
          {bubbles.map((b) => (
            <radialGradient key={`bubble-grad-${b.index}`} id={`bubble-grad-${b.index}`} cx="35%" cy="35%">
              <stop offset="0%" stopColor={b.color.gradient[0]} stopOpacity={0.95} />
              <stop offset="100%" stopColor={b.color.gradient[1]} stopOpacity={0.85} />
            </radialGradient>
          ))}
        </defs>

        {/* Bubbles */}
        {bubbles.map((b) => {
          const isHovered = hoveredIndex === b.index;
          const showLabel = b.r > 28;

          return (
            <g
              key={b.index}
              onMouseMove={(e) => {
                const rect = containerRef.current?.getBoundingClientRect();
                if (rect) {
                  setTooltip({
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top - 12,
                    label: b.label,
                    value: b.value,
                  });
                }
                setHoveredIndex(b.index);
              }}
              onMouseLeave={() => {
                setTooltip(null);
                setHoveredIndex(null);
              }}
              style={{ cursor: 'pointer' }}
            >
              {/* Shadow */}
              <circle
                cx={b.x + 2}
                cy={b.y + 2}
                r={isHovered ? b.r + 3 : b.r}
                fill="rgba(0,0,0,0.06)"
                style={{ transition: 'r 0.2s ease' }}
              />
              {/* Main circle */}
              <circle
                cx={b.x}
                cy={b.y}
                r={isHovered ? b.r + 3 : b.r}
                fill={`url(#bubble-grad-${b.index})`}
                stroke={isHovered ? b.color.dark : 'white'}
                strokeWidth={isHovered ? 2.5 : 2}
                style={{
                  transition: 'r 0.2s ease, stroke 0.15s ease',
                  opacity: hoveredIndex !== null && !isHovered ? 0.45 : 1,
                }}
              />
              {/* Label inside bubble */}
              {showLabel && (
                <>
                  <text
                    x={b.x}
                    y={b.y - (b.r > 40 ? 6 : 2)}
                    textAnchor="middle"
                    fill="white"
                    fontSize={b.r > 50 ? 13 : b.r > 35 ? 11 : 9.5}
                    fontWeight={600}
                    style={{ pointerEvents: 'none', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                  >
                    {b.label.length > (b.r > 50 ? 14 : 10)
                      ? b.label.substring(0, b.r > 50 ? 12 : 8) + '..'
                      : b.label}
                  </text>
                  {b.r > 35 && (
                    <text
                      x={b.x}
                      y={b.y + (b.r > 50 ? 14 : 12)}
                      textAnchor="middle"
                      fill="rgba(255,255,255,0.85)"
                      fontSize={b.r > 50 ? 15 : 12}
                      fontWeight={700}
                      style={{ pointerEvents: 'none' }}
                    >
                      {b.value.toLocaleString()}
                    </text>
                  )}
                </>
              )}
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute pointer-events-none z-10"
          style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -100%)' }}
        >
          <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-xs whitespace-nowrap">
            <div className="font-semibold">{tooltip.label}</div>
            <div className="text-gray-300 mt-0.5">
              {tooltip.value.toLocaleString()} candidates ({((tooltip.value / total) * 100).toFixed(1)}%)
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BubbleChart;
